import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheckIcon,
  ArrowLeftIcon,
  PhoneIcon,
  EnvelopeIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "react-hot-toast";

const ConsensiPrivacy = () => {
  const { user, acceptPrivacyPolicy, revokePrivacyPolicy, logout } = useAuth();
  const [revoking, setRevoking] = useState(false);

  const privacyAcceptedAt = useMemo(
    () => user?.privacy_policy_accepted_at || null,
    [user?.privacy_policy_accepted_at]
  );

  const handleAccept = async () => {
    const result = await acceptPrivacyPolicy();
    if (result?.success) {
      toast.success("Consenso registrato correttamente.");
    }
  };

  const handleRevoke = () => {
    if (revoking) return;
    setRevoking(true);
    revokePrivacyPolicy()
      .then((result) => {
        if (!result?.success) {
          throw new Error(result?.error || "Operazione non riuscita");
        }
        toast(
          "Consenso revocato. Vieni disconnesso come richiesto.",
          { icon: "ℹ️", duration: 5000 }
        );
        logout();
      })
      .catch((error) => {
        console.error("Errore revoca privacy:", error);
        toast.error("Impossibile revocare al momento. Riprova più tardi.");
      })
      .finally(() => setRevoking(false));
  };

  return (
    <div className="max-w-3xl mx-auto p-6">
      <div className="mb-6 flex items-center gap-3">
        <ShieldCheckIcon className="h-8 w-8 text-primary-600" />
        <div>
          <p className="text-sm text-gray-500 uppercase tracking-wide">
            Informativa
          </p>
          <h1 className="text-3xl font-bold text-gray-900">
            Consensi Privacy
          </h1>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg p-6 space-y-6">
        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-gray-900">
            Finalità della piattaforma
          </h2>
          <p className="text-gray-700">
            Planner Turni è utilizzata dalle congregazioni per gestire:
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-1">
            <li>Registrazione dei volontari e delle relative deleghe;</li>
            <li>Organizzazione dei turni, disponibilità e assegnazioni;</li>
            <li>Gestione delle postazioni, documenti di autorizzazione e notifiche;</li>
            <li>Tracciamento delle attività (cronologia, statistiche, log operativi).</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-gray-900">
            Tipologie di dati trattati
          </h2>
          <ul className="list-disc list-inside text-gray-700 space-y-1">
            <li>Dati identificativi: nome, cognome, congregazione di appartenenza;</li>
            <li>Contatti: email e numero telefonico;</li>
            <li>Dati organizzativi: ruolo, stato attività, deleghe, disponibilità, assegnazioni;</li>
            <li>Log di accesso e cronologia operazioni per esigenze di sicurezza;</li>
            <li>Eventuali documenti caricati per autorizzazioni o comunicazioni interne.</li>
          </ul>
          <p className="text-gray-700">
            Non vengono trattati dati particolari diversi da quelli sopra indicati e i dati non vengono
            mai ceduti o rivenduti a terzi.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-gray-900">
            Basi giuridiche e tempi di conservazione
          </h2>
          <p className="text-gray-700">
            Il trattamento è basato sul legittimo interesse della congregazione e sul consenso
            dell&apos;utente. I dati operativi sono conservati per tutta la durata dell&apos;utilizzo
            della piattaforma e cancellati o anonimizzati quando non più necessari oppure su richiesta
            dell&apos;interessato, fatte salve eventuali esigenze di legge.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-gray-900">Sicurezza</h2>
          <p className="text-gray-700">
            L&apos;applicazione utilizza connessioni cifrate, controllo degli accessi, logging delle
            operazioni e procedure di backup per ridurre il rischio di accesso non autorizzato o
            perdita dei dati.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-gray-900">
            Diritti degli interessati
          </h2>
          <p className="text-gray-700">
            Puoi richiedere l&apos;accesso, la rettifica, la cancellazione o la limitazione del
            trattamento dei tuoi dati. È inoltre possibile revocare il consenso in qualunque momento
            dalla presente pagina.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-gray-900">
            Responsabile del trattamento
          </h2>
          <div className="bg-gray-50 rounded-lg p-4 text-gray-700 space-y-2">
            <p className="font-semibold text-gray-900">
              Davide Arena — Data Protection Officer (Certificato EIPASS)
            </p>
            <p className="flex items-center gap-2">
              <PhoneIcon className="h-5 w-5 text-primary-600" />
              <span>329 081 5425</span>
            </p>
            <p className="flex items-center gap-2">
              <EnvelopeIcon className="h-5 w-5 text-primary-600" />
              <a
                href="mailto:arena@advpro.it"
                className="text-primary-600 hover:underline"
              >
                arena@advpro.it
              </a>
            </p>
            <p>I dati non saranno mai ceduti o rivenduti a terzi.</p>
          </div>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-gray-900">
            Stato del consenso e azioni disponibili
          </h2>
          <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-700 space-y-2">
            <p className="font-semibold text-gray-900">
              Stato attuale del consenso:
            </p>
            {privacyAcceptedAt ? (
              <p>
                Hai accettato la privacy policy il{" "}
                <strong>
                  {new Date(privacyAcceptedAt).toLocaleString("it-IT")}
                </strong>
              </p>
            ) : (
              <p className="text-red-600">
                Non hai ancora accettato la privacy policy.
              </p>
            )}
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
                onClick={handleAccept}
              >
                Accetto / rinnovo il consenso
              </button>
              <button
                className="px-4 py-2 border border-red-300 text-red-600 rounded-md hover:bg-red-50 disabled:opacity-50"
                onClick={handleRevoke}
                disabled={revoking}
              >
                {revoking ? "Revoca in corso..." : "Revoca e disconnetti"}
              </button>
            </div>
          </div>
        </section>
      </div>

      <div className="mt-6">
        <Link
          to="/impostazioni"
          className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Torna alle Impostazioni
        </Link>
      </div>
    </div>
  );
};

export default ConsensiPrivacy;
